export type TemplateViewVisibility = 'Private' | 'Shared' | 'Public' | 'Global';

/**
 * One enriched row from the v2 template-view procs (cmdTemplateList_v2 /
 * cmdTemplateViewSave_v2 / cmdTemplateViewLayoutUpdate), as serialized by
 * /api/cmd: camelCase, layout states as JSON strings, every flag a real number
 * (the procs CAST them to INT precisely so this shape holds).
 * Error sentinels (templateViewID -1/-2) are NOT this shape — check
 * isErrorRow() from template-view.service before treating a result as a row.
 */
export interface TemplateViewRow {
    templateViewID: number;
    pageCode: string;
    templateViewName: string;
    columnState?: string;
    filterState?: string;
    sortState?: string;
    groupState?: string;
    publicView?: number;
    globalView?: number;
    pivotView?: number;
    status?: string;
    description?: string;
    visibility?: TemplateViewVisibility;
    subscribed?: number;          // 1 = shown in my dropdown, 0 = hidden (no row = shown)
    canEdit?: number;             // owner | Editor share (while Shared/Public) | admin on Global/Public
    canManage?: number;           // owner | admin on Global/Public (a Shared view is owner-managed only)
    hasShare?: number;            // caller has an active share row on this view
    isOwner?: number;
    accessLevel?: 'Owner' | 'Editor' | 'Viewer';
    ownerName?: string;
    ownerEmail?: string;
    createdByID?: number;
    createdDate?: string;
    updatedByID?: number;
    updatedDate?: string;
    templateSortOrder?: number;
    districtID?: number;
    isAdmin?: number;             // caller-scoped constant from the list proc
}

/** "Tyler Baker" -> "TB" — avatar initials for share rows / the user picker. */
export function initialsOf(name: string): string {
    return (name || '')
        .split(' ')
        .filter(w => !!w)
        .map(w => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
}

export class TemplateView {
    templateViewID?: number = 0;
    pageCode?: string = '';
    templateViewName: string;
    groupState?: object[];
    columnState?: object[];
    filterState?: {};
    sortState?: object[];
    pivotView?: boolean = false;
    publicView?: boolean = false;
    globalView?: boolean = false;
    status: string = 'Active';

    // ── Manage Views (v2) fields — additive & optional; legacy cached rows lack them
    description?: string;
    visibility?: TemplateViewVisibility;
    subscribed?: number;          // 1 = shown in my dropdown, 0 = hidden (server default = 1)
    canEdit?: number;             // server-computed: owner | Editor share (while Shared/Public) | admin on Global/Public
    canManage?: number;           // server-computed: owner | admin on Global/Public (Shared = owner-managed only)
    hasShare?: number;            // caller has an active share row on this view
    isOwner?: number;
    accessLevel?: 'Owner' | 'Editor' | 'Viewer';
    ownerName?: string;
    ownerEmail?: string;
    updatedDate?: string;
    createdByID?: number;
    templateSortOrder?: number;
    districtID?: number;
    isAdmin?: number;             // caller-scoped constant from the list proc
}

// One active row of dbo.TemplateViewShare (cmdTemplateViewShareList shape)
export interface TemplateViewShare {
    templateViewShareID?: number;
    templateViewID?: number;
    internalContactsID: number;
    firstName?: string;
    lastName?: string;
    name?: string;                // convenience: first + last
    email?: string;
    accessLevel: 'Viewer' | 'Editor';
}

// Directory entry for the share picker (from GET /api/User)
export interface UserDirectoryEntry {
    internalContactsID: number;
    name: string;
    email: string;
}
